/**
 * Turnstile status: init, update, get (Proxy-based).
 * @package AdvancedTrackingManager
 */

import type { TurnstileStatus } from '@lib/types';
import { deleteCookie, getCookie, setCookie } from '@lib/cookies';
import { createLogger } from '@lib/logger';
import { TEST_SITEKEYS } from './constants';

const COOKIE_NAME = 'atm_turnstile_status';
const COOKIE_MAX_AGE_SEC = 604800; // 1 week (7 * 24 * 60 * 60)
const COOKIE_MAX_AGE_MIN = COOKIE_MAX_AGE_SEC / 60;

const ALLOWED_KEYS: (keyof TurnstileStatus)[] = [
  'verified',
  'serverVerified',
  'pending',
  'failed',
  'challengeShown',
  'token',
];

export const initStatus = (siteKey: string, debugMode: boolean): void => {
  const w = globalThis as unknown as Window;
  if (typeof w.atmTurnstileStatus !== 'undefined') return;

  const log = createLogger('ATM Turnstile', debugMode);
  const cookieStatus = getCookie(COOKIE_NAME);
  let initialStatus: TurnstileStatus = {
    verified: false,
    serverVerified: false,
    pending: true,
    failed: false,
    challengeShown: false,
    token: null,
  };

  if (cookieStatus) {
    try {
      const parsed = JSON.parse(cookieStatus) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object') {
        log.debug('⚠️ Invalid cookie structure - ignoring');
        return;
      }
      if (parsed.timestamp) {
        const cookieTime = new Date(parsed.timestamp as string).getTime();
        const ageMinutes = (Date.now() - cookieTime) / (1000 * 60);
        if (ageMinutes > COOKIE_MAX_AGE_MIN) {
          log.debug(
            `⚠️ Cookie too old (${ageMinutes.toFixed(
              1
            )} minutes, limit ${COOKIE_MAX_AGE_MIN.toFixed(1)}) - ignoring`
          );
          deleteCookie(COOKIE_NAME);
          return;
        }
      }

      if (
        parsed.verified === true &&
        parsed.token &&
        parsed.sitekey === siteKey &&
        siteKey !== TEST_SITEKEYS.INTERACTIVE
      ) {
        const fullToken =
          sessionStorage.getItem('atm_turnstile_token') ?? (parsed.token as string) ?? null;
        initialStatus = {
          verified: true,
          serverVerified: (parsed.serverVerified as boolean) ?? false,
          pending: false,
          failed: false,
          challengeShown: false,
          token: fullToken,
        };
        log.debug('✅ Restored Turnstile status from cookie', initialStatus);
      }
      if (parsed.sitekey !== siteKey) {
        log.debug('⚠️ Cookie sitekey mismatch - not restoring status', {
          cookieSitekey: parsed.sitekey,
          currentSitekey: siteKey,
        });
      }
    } catch (e) {
      log.debug('⚠️ Failed to parse Turnstile status cookie', e);
    }
  }

  const statusTarget = initialStatus;
  if (typeof Proxy !== 'undefined') {
    w.atmTurnstileStatus = new Proxy(statusTarget, {
      set(target, property: string, value: unknown) {
        if (debugMode) {
          log.debug('🔒 Status property changed via direct assignment', {
            property,
            oldValue: (target as unknown as Record<string, unknown>)[property],
            newValue: value,
          });
        }
        (target as unknown as Record<string, unknown>)[property] = value;
        return true;
      },
      deleteProperty() {
        if (debugMode) log.debug('⚠️ Attempt to delete status property blocked');
        return false;
      },
    }) as TurnstileStatus;
  } else {
    w.atmTurnstileStatus = statusTarget;
  }
  log.debug('✅ Turnstile status initialized', w.atmTurnstileStatus);
};

export const updateStatus = (
  updates: Partial<TurnstileStatus>,
  siteKey: string,
  debugMode: boolean
): void => {
  const w = globalThis as unknown as Window;
  if (typeof w.atmTurnstileStatus === 'undefined') return;

  const log = createLogger('ATM Turnstile', debugMode);
  const filtered: Partial<TurnstileStatus> = {};
  for (const key of ALLOWED_KEYS) {
    if (key in updates) {
      (filtered as Record<string, unknown>)[key] = updates[key];
    }
  }

  Object.assign(w.atmTurnstileStatus, filtered);

  const cookieData = {
    verified: w.atmTurnstileStatus!.verified,
    serverVerified: w.atmTurnstileStatus!.serverVerified ?? false,
    failed: w.atmTurnstileStatus!.failed,
    token: w.atmTurnstileStatus!.token
      ? `${w.atmTurnstileStatus!.token.slice(0, 20)}...`
      : null,
    timestamp: new Date().toISOString(),
    sitekey: siteKey,
  };
  setCookie(COOKIE_NAME, JSON.stringify(cookieData), COOKIE_MAX_AGE_SEC);

  sessionStorage.setItem(
    'atm_turnstile_checked',
    w.atmTurnstileStatus!.verified ? 'true' : 'false'
  );
  sessionStorage.setItem(
    'atm_turnstile_server_verified',
    w.atmTurnstileStatus!.serverVerified ? 'true' : 'false'
  );
  sessionStorage.setItem('atm_turnstile_failed', w.atmTurnstileStatus!.failed ? 'true' : 'false');
  if (w.atmTurnstileStatus!.token) {
    sessionStorage.setItem('atm_turnstile_token', w.atmTurnstileStatus!.token);
  } else {
    sessionStorage.removeItem('atm_turnstile_token');
    sessionStorage.removeItem('atm_turnstile_server_verified');
  }

  // Plain object with only primitives (no Proxy); safe for CustomEvent and any cloning
  const rawToken = w.atmTurnstileStatus!.token;
  const token =
    rawToken !== null && typeof rawToken === 'string' ? rawToken : null;
  const statusCopy: TurnstileStatus = {
    verified: Boolean(w.atmTurnstileStatus!.verified),
    serverVerified: Boolean(w.atmTurnstileStatus!.serverVerified ?? false),
    pending: Boolean(w.atmTurnstileStatus!.pending),
    failed: Boolean(w.atmTurnstileStatus!.failed),
    challengeShown: Boolean(w.atmTurnstileStatus!.challengeShown ?? false),
    token,
  };
  try {
    document.dispatchEvent(
      new CustomEvent('atm_turnstile_status_changed', { detail: statusCopy })
    );
    log.debug('📢 Dispatched atm_turnstile_status_changed event', statusCopy);
  } catch (err) {
    if (debugMode && typeof console !== 'undefined' && console.warn) {
      console.warn('[ATM Turnstile] Failed to dispatch status event', err);
    }
  }
};

export const getStatus = (): TurnstileStatus | undefined =>
  (globalThis as unknown as Window).atmTurnstileStatus;
